import type { BuiltInFilterKey } from '@datagouv/components-next'

export const CUSTOM_FILTER_TYPES = ['select', 'organization_custom'] as const
export type CustomFilterType = (typeof CUSTOM_FILTER_TYPES)[number]

export interface TopicsConf {
  can_add_topics: {
    everyone: boolean
    authorized_users: string[]
  }
}

export interface TopicItemConf {
  id: string
  name: string
}

export interface StaticPageConfig {
  title: string
  id: string
  route: string
  url: string
  meta?: {
    title?: string
    description?: string
  }
}

export interface MenuConfig {
  text: string
  to: string
  external?: boolean
}

export interface PageFilterValueConf {
  id: string
  name: string
  parent?: string
}

export interface PageFilterFormConf {
  required: boolean
}

export interface PageFilterConf {
  name: string
  id: string
  type: CustomFilterType | BuiltInFilterKey
  advanced?: boolean
  // Page keys this filter applies to in multi-type search. Defaults to the owning page only.
  applies_to_pages?: string[]
  child: string | null
  color: string | null
  default_option: string | null
  default_value: string | boolean | null
  use_filter_prefix: boolean | null
  api_param: string | null
  form: PageFilterFormConf | null
  values: PageFilterValueConf[]
}

export type PageUniverseQueryConf = {
  [key: string]: string | number | boolean
}

export type PageBannerConf = {
  title: string
  content: string
}

export type PageSearchConf = {
  input: string
  placeholder?: string | null
}

export type PageLabelsConf = {
  singular: string
  plural: string
  extended: string
  feminine?: boolean
}

export type PageObjectType = 'datasets' | 'dataservices' | 'topics'

export type PageConf = {
  object_type: PageObjectType
  list_all: boolean
  filter_prefix: string | null
  universe_query: PageUniverseQueryConf | null
  title: string
  breadcrumb_title: string | null
  icon?: string
  labels: PageLabelsConf
  search: PageSearchConf
  banner: PageBannerConf | null
  default_sort: string | null
  resources_tabs: {
    discussions: {
      display: boolean
      create: boolean
      notify_url_override?: boolean
    }
    datasets: {
      display: boolean
    }
    reuses: {
      display: boolean
    }
  }
  editable: boolean
  meta?: {
    title?: string
    description?: string
  }
  filters: PageFilterConf[]
}

export type PagesConf = {
  [key: string]: PageConf
}

export type DatasetsConf = {
  add_to_topic: {
    page: string
  } | null
  harvest_backends_quality_warning: string[]
  show_extended_information_panel: boolean
  resources_explorer_enabled?: boolean
}

interface Logo {
  src?: string
  width?: string
  height?: string
}

export type HeaderConf = {
  admin_shortcut: boolean
  logo?: Logo
  title_image?: Logo
  description?: string
  beta: boolean
  search: {
    display: boolean
    placeholder?: string
  }
}

type FooterExternalLink = {
  label: string
  href: string
  title: string
}

export interface CmsPageConfig {
  title: string
  id: string // id or slug of the page on data.gouv.fr
  route: string
}

// a selectable, tag-marked collection of CMS pages (e.g. news articles)
export interface CmsCategoryConfig {
  id: string
  label: string
  route_prefix: string
}

export interface CmsConfig {
  enabled: boolean
  topic_id?: string // scopes the CMS page list to pages attached to this topic
  pages?: CmsPageConfig[]
  categories?: CmsCategoryConfig[]
}

type FooterMandatoryLink = {
  label: string
  to: string | undefined
  href: string | undefined
  title?: string
}

export type FooterConf = {
  logo?: Logo
  phrase?: string
  external_links?: FooterExternalLink[]
  mandatory_links?: FooterMandatoryLink[]
}

export type WebsiteConfig = {
  title: string
  rf_title: string
  seo?: {
    canonical_url?: string
    meta?: {
      keywords?: string
      description?: string
      robots?: string
    }
  }
  header: HeaderConf
  footer: FooterConf
  cms?: CmsConfig
}

// https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/
export type SentryConfig = {
  dsn: string // Mandatory to send errors to Sentry
  domain_url?: string // Mandatory to send sourcemaps to Sentry. This is not used in sentry options, it is only used in vite.config.mts to send sourcemaps to the correct domain.
  environment?: string
  tracePropagationTargets?: RegExp[]
  tracesSampleRate?: number // Enables performance monitoring (page loads, navigations, requests) when set
  replaysSessionSampleRate?: number
  replaysOnErrorSampleRate?: number
}

export type OrganizationsConfig = {
  datasets?: string
  dataservices?: string
  bouquets?: string
  page?: {
    breadcrumb_title?: string
    labels?: {
      singular?: string
    }
    meta?: {
      title?: string
      description?: string
    }
  }
}
